'use client'

import { ALL_FILTER_VALUE, type FilterOption } from '@/components/patterns/table-filter-tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

/**
 * Same prop shape as `TableFilterTabs` (a drop-in swap) but renders as a `Select` dropdown
 * instead of a segmented tab row — for filter dimensions with enough values that a tab row
 * would need to scroll sideways to show them all. Counts still show, just inside the closed-off
 * dropdown rather than always-visible on a tab.
 */
export function TableFilterSelect<T extends string>({
  value,
  onValueChange,
  allLabel = 'All',
  allCount,
  options,
  className,
}: {
  value: T | typeof ALL_FILTER_VALUE
  onValueChange: (value: T | typeof ALL_FILTER_VALUE) => void
  allLabel?: string
  allCount: number
  options: FilterOption<T>[]
  className?: string
}) {
  if (options.length < 2) return null

  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as T | typeof ALL_FILTER_VALUE)}>
      {/* Matches the outline `Button` trigger used by the adjacent staff/caller filter
          (e.g. `admin-calls-view.tsx`'s "All callers" dropdown) so the two sit side by side
          looking like one filter row, not two differently-styled controls — `SelectTrigger`'s
          own defaults (h-8, px-3, border-input) run taller and wider than that button's sm size,
          so every mismatched utility is overridden here via the same modifier bucket
          (`data-[size=sm]:h-7`) rather than fighting Tailwind's cascade order. */}
      <SelectTrigger
        size="sm"
        className={cn(
          'border-border bg-background hover:bg-muted hover:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50 w-fit gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] shadow-none data-[size=sm]:h-7',
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_FILTER_VALUE}>
          {allLabel} ({allCount})
        </SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.count == null ? option.label : `${option.label} (${option.count})`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
